import React, { useState, useEffect } from 'react';
import axiosInstance from 'utils/axios-instance';
import Swal from 'sweetalert2';

type Member = {
    userId: number;
    nickname: string;
    joinedAt: string;
    postCount: number;
    replyCount: number;
}

interface Props {
    communityUuid: string;
}

const MemberManagement: React.FC<Props> = ({ communityUuid }) => {
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchMembers();
    }, [communityUuid]);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(
                `/api/community/admin/members?communityUuid=${communityUuid}`
            );
            setMembers(response.data);
        } catch (error) {
            console.error('회원 목록 조회 실패:', error);
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: '회원 목록을 불러오는 중 오류가 발생했습니다.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleKick = async (userId: number, nickname: string) => {
        const result = await Swal.fire({
            title: '회원 추방',
            text: `${nickname} 님을 추방하시겠습니까?`,
            icon: 'warning',
            input: 'textarea',
            inputLabel: '추방 사유',
            inputPlaceholder: '추방 사유를 입력하세요...',
            showCancelButton: true,
            confirmButtonText: '추방',
            cancelButtonText: '취소',
            confirmButtonColor: '#d33',
            inputValidator: (value) => {
                if (!value) {
                    return '추방 사유를 입력해주세요!';
                }
            }
        });

        if (result.isConfirmed && result.value) {
            try {
                const response = await axiosInstance.post('/api/community/admin/kick', {
                    communityUuid,
                    targetUserId: userId,
                    reason: result.value
                });

                if (response.data.statusCode === 200) {
                    Swal.fire({
                        icon: 'success',
                        title: '완료',
                        text: response.data.message,
                    });
                    fetchMembers();
                }
            } catch (error: any) {
                console.error('회원 추방 실패:', error);
                Swal.fire({
                    icon: 'error',
                    title: '오류',
                    text: error.response?.data?.message || '회원 추방 중 오류가 발생했습니다.',
                });
            }
        }
    };

    const filteredMembers = members.filter(member =>
        member.nickname.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
                <p>회원 목록을 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div className="member-management">
            <div className="management-header">
                <h2>회원 관리</h2>
                <p className="member-count">총 {members.length}명</p>
            </div>

            <div className="search-bar">
                <input
                    type="text"
                    placeholder="회원 닉네임 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {filteredMembers.length === 0 ? (
                <div className="empty-state">
                    <p>회원이 없습니다.</p>
                </div>
            ) : (
                <div className="members-table-container">
                    <table className="members-table">
                        <thead>
                            <tr>
                                <th>닉네임</th>
                                <th>가입일</th>
                                <th>게시글</th>
                                <th>댓글</th>
                                <th>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMembers.map(member => (
                                <tr key={member.userId}>
                                    <td className="nickname-cell">{member.nickname}</td>
                                    <td>{new Date(member.joinedAt).toLocaleDateString('ko-KR')}</td>
                                    <td>{member.postCount}</td>
                                    <td>{member.replyCount}</td>
                                    <td>
                                        <button
                                            className="kick-button"
                                            onClick={() => handleKick(member.userId, member.nickname)}
                                        >
                                            추방
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default MemberManagement;

